import * as LayoutComponents from "../LayoutComponents";
import Button from "ui/atoms/Button/Button";
import { motion } from "framer-motion";
import { useAnimation } from "./useCircleAnimation";
import { useTimeoutEffect } from "@react-hookz/web";

interface Props {
  onNext: () => void;
  mobileToolbarStyle: LayoutComponents.MobileToolbarStyle;

  ctaText?: string;
  title?: string;
  subtitle?: string;

  countdownSeconds?: number;
}

export default function SuccessScreen({
  onNext,
  mobileToolbarStyle,

  ctaText = "Next",
  title = "Correct!",
  subtitle = "Next one coming up in...",

  countdownSeconds = 3,
}: Props) {
  const { ref, countdown, pathLength } = useAnimation(countdownSeconds);

  useTimeoutEffect(onNext, countdownSeconds * 1000);

  return (
    <>
      <LayoutComponents.Toolbar mobileStyle={mobileToolbarStyle}>
        <div className={"flex items-center justify-between"}>
          <div>
            <div className={"hidden md:block"}>
              <motion.h1
                className={"font-brik text-4xl leading-none text-emerald-500"}
                initial={{ opacity: 0, y: -10 }}
                animate={{ y: 0, opacity: 1 }}
              >
                {title}
              </motion.h1>

              <motion.p
                className={"text-base leading-none text-white"}
                initial={{ opacity: 0, y: -10 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                {subtitle}
              </motion.p>
            </div>
          </div>

          <Button theme={"default"} onClick={onNext} size="large">
            {ctaText}
          </Button>
        </div>
      </LayoutComponents.Toolbar>

      <LayoutComponents.Container>
        <div className={"mb-2 md:hidden"}>
          <motion.h1
            className={"font-brik text-4xl leading-none text-emerald-500"}
            initial={{ opacity: 0, y: -10 }}
            animate={{ y: 0, opacity: 1 }}
          >
            {title}
          </motion.h1>

          <motion.p
            className={"text-base leading-none text-white"}
            initial={{ opacity: 0, y: -10 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            {subtitle}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className={
            "relative flex items-center justify-center overflow-hidden bg-emerald-500 py-4 text-white md:py-10"
          }
        >
          <svg
            viewBox={"0 0 100 100"}
            className={
              "absolute left-1/2 top-1/2 min-h-full min-w-full -translate-x-1/2 -translate-y-1/2 scale-[250%] object-cover"
            }
          >
            <defs>
              <circle
                ref={ref}
                id="circle"
                cx="50"
                cy="50"
                r="50"
                className={"origin-center -rotate-90"}
                style={{
                  strokeDasharray:
                    countdown > 0 ? `${pathLength}px` : undefined,
                }}
              />

              <clipPath id="inside">
                <use xlinkHref="#circle" />
              </clipPath>
            </defs>

            <motion.use
              key={countdown}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
              xlinkHref="#circle"
              clipPath="url(#inside)"
              className={"fill-none stroke-emerald-600"}
              strokeWidth="100%"
            />
          </svg>

          <div
            className={
              "absolute bottom-0 left-1/2 top-0 w-[2px] -translate-x-1/2 bg-emerald-900"
            }
          />

          <div
            className={
              "absolute left-0 right-0 top-1/2 h-[2px] -translate-y-1/2 bg-emerald-900"
            }
          />

          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={
              "relative flex h-[350px] w-[350px] items-center justify-center rounded-full border-2 border-white p-10 text-center"
            }
          >
            <div
              className={"absolute inset-4 rounded-full border-2 border-white"}
            />

            <motion.div
              key={countdown}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={"z-10 text-[250px] leading-none tracking-tighter"}
            >
              {countdown}
            </motion.div>
          </motion.div>
        </motion.div>
      </LayoutComponents.Container>
    </>
  );
}
